import React, { useEffect, useMemo, useState } from "react";
import withStyles from "isomorphic-style-loader/withStyles";
import { connect } from "react-redux";
import { withFirebase } from "react-redux-firebase";
import logo from "../../../media/img/vs-logo-blue.png";
import PrimaryButton from "../../../toolkit/primaryButton";
import LoadingScreen from "../../../toolkit/loadingScreen";
import AdminPhoneInlineForm from "../../../toolkit/adminPhoneInlineForm";
import { withRouter, RouterProps } from "../../../utils/withRouter";
import { serviceCaller, ServiceCallerProps } from "../../composition";
import {
  adminHasVerifiedPhone,
  resolveAdminPhonePromptContext,
  shouldShowPostLoginPhoneScreen,
  skipPostLoginPhoneScreen,
} from "../../../utils/adminPhonePrompt";
import s from "./styles.scss";

interface Props extends RouterProps, ServiceCallerProps {
  firebase: any;
  userId: string;
  currentCharityId: string;
}

function PostLoginPhonePromptView(props: Props) {
  const { firebase, userId, currentCharityId, navigate, location, config } = props;
  const [ready, setReady] = useState(false);
  const [showPhoneForm, setShowPhoneForm] = useState(false);

  const returnTo = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const raw = params.get("returnTo");
    if (raw && raw.startsWith("/")) {
      return raw;
    }
    return "/contacts";
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    let detach: (() => void) | undefined;

    const run = async () => {
      const u = firebase.auth().currentUser;
      if (!u) {
        if (!cancelled) {
          navigate("/login", { replace: true });
        }
        return;
      }

      const show = await shouldShowPostLoginPhoneScreen(
        firebase,
        userId,
        currentCharityId
      );
      if (!cancelled && !show) {
        navigate(returnTo, { replace: true });
        return;
      }

      const ctx = await resolveAdminPhonePromptContext(
        u,
        userId,
        currentCharityId
      );
      if (!ctx) {
        if (!cancelled) {
          setReady(true);
        }
        return;
      }

      const db = firebase.database();
      const phoneRef = db.ref(
        `${ctx.charityId}/users/${ctx.adminId}/phoneNumber`
      );
      const onPhoneChange = async () => {
        const hasPhone = await adminHasVerifiedPhone(
          db,
          ctx.charityId,
          ctx.adminId
        );
        if (hasPhone && !cancelled) {
          navigate(returnTo, { replace: true });
        }
      };
      phoneRef.on("value", onPhoneChange);
      detach = () => phoneRef.off("value", onPhoneChange);

      if (!cancelled) {
        setReady(true);
      }
    };

    void run();

    return () => {
      cancelled = true;
      if (detach) {
        detach();
      }
    };
  }, [firebase, userId, currentCharityId, navigate, returnTo]);

  const onNotNow = () => {
    skipPostLoginPhoneScreen();
    navigate(returnTo, { replace: true });
  };

  const onPhoneComplete = () => {
    navigate(returnTo, { replace: true });
  };

  if (!ready) {
    return <LoadingScreen />;
  }

  const authServiceUrl = config?.authServiceUrl;

  return (
    <div className={s.page}>
      <div className={s.card}>
        <img src={logo} alt="ViewSpark" className={s.logo} />
        <p className={s.copy}>
          Add an extra layer of protection to your account by adding your phone
          number to receive your two-factor authentication codes through SMS.
        </p>
        {!showPhoneForm ? (
          <PrimaryButton
            type="button"
            className={s.primaryAction}
            onClick={() => setShowPhoneForm(true)}
          >
            Add phone number
          </PrimaryButton>
        ) : authServiceUrl ? (
          <div className={s.formSection}>
            <AdminPhoneInlineForm
              authServiceUrl={authServiceUrl}
              onComplete={onPhoneComplete}
            />
          </div>
        ) : (
          <p className={s.configError}>Configuration is still loading. Please refresh.</p>
        )}
        <button type="button" className={s.notNow} onClick={onNotNow}>
          Not now
        </button>
      </div>
    </div>
  );
}

const mapStateToProps = state => ({
  userId: state.user.id,
  currentCharityId: state.user.currentCharity?.id || "",
});

export default withFirebase(
  serviceCaller(
    withRouter(
      connect(mapStateToProps)(withStyles(s)(PostLoginPhonePromptView))
    )
  )
);
